import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  pushSupported,
  subscribePush,
  getCurrentPushSubscription,
  isPushDismissed,
  dismissPush,
} from "@/lib/pwa";
import { toast } from "sonner";

/**
 * Smart push opt-in banner.
 * Shown on /dashboard only for logged-in users when:
 *  - browser supports push
 *  - permission isn't already denied
 *  - user hasn't already subscribed
 *  - user hasn't dismissed within the cooldown window
 */
export default function PushOptInBanner() {
  const { user, refresh } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user) return;
      if (!pushSupported()) return;
      if (Notification.permission === "denied") return;
      if (user.push_opt_in) return;
      if (isPushDismissed()) return;
      const sub = await getCurrentPushSubscription();
      if (sub) return;
      // Defer slightly so we don't compete with onboarding
      setTimeout(() => setShow(true), 1200);
    })();
  }, [user]);

  if (!show) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notifications enabled. We'll nudge you at the right moments.");
      await refresh?.();
      setShow(false);
    } catch (e) {
      toast.error(e?.message || "Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    dismissPush();
    setShow(false);
  };

  return (
    <div
      className="brut p-4 md:p-5 mb-6 flex items-start gap-3"
      style={{
        background:
          "linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(16,185,129,0.05) 100%)",
        borderColor: "rgba(37,99,235,0.25)",
      }}
      data-testid="push-optin-banner"
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: "var(--accent, #2563EB)" }}
      >
        <Bell size={16} color="#fff" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[var(--ink)]">
          Get gentle nudges, three times a day.
        </div>
        <div className="text-sm text-[var(--ink-2)] mt-0.5">
          7–9 AM: today's challenge · 12–2 PM: quick lesson · 7–9 PM: streak reminder. No spam.
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={enable}
            disabled={busy}
            className="btn-duo"
            style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
            data-testid="push-optin-enable"
          >
            {busy ? <span className="spinner" /> : <><Bell size={13} /> Turn on reminders</>}
          </button>
          <button
            onClick={close}
            className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-2)]"
            data-testid="push-optin-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
      <button
        onClick={close}
        aria-label="Dismiss"
        className="text-[var(--ink-3)] hover:text-[var(--ink)] flex-shrink-0"
      >
        <X size={16} />
      </button>
    </div>
  );
}
